import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  InputAdornment,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
  Pagination,
  Paper,
  createTheme,
  useMediaQuery,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArticleIcon from "@mui/icons-material/Article";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import * as XLSX from "xlsx";
import CDRExcelAnalyzerPage from "./CDRExcelAnalyzerPage";
import TransactionTree from "./TransactionTree";
import LoginPage from "./LoginPage";
import AdminPanel from "./AdminPanel";
import BlogHome from "./blog/BlogHome";
import BlogCategory from "./blog/BlogCategory";
import BlogPost from "./blog/BlogPost";
import BlogTag from "./blog/BlogTag";
import { AboutPage, ContactPage, CookiePolicyPage, EditorialPolicyPage, PrivacyPolicyPage, TermsPage } from "./TrustPages";
import { CDR_FAQS, CdrAeoContent } from "./AeoContent";
import SeoHelmet from "./seo/SeoHelmet";
import { absoluteUrl, buildFaqSchema, buildSoftwareApplicationSchema, buildWebPageSchema, buildWebSiteSchema } from "./seo/schema";
import { useAuth } from "./auth";
import {
  VISITOR_STORAGE_KEYS,
  clearVisitorData,
  loadVisitorData,
  saveVisitorData,
} from "./visitorStorage";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";

const drawerWidth = 240; // px
const collapsedDrawerWidth = 72; // px
const API_BASE = process.env.REACT_APP_API_BASE || "";
const ACCESS_PUBLIC = "public";
const ACCESS_AUTH = "auth";

const APP_THEMES = {
  midnightSlate: {
    label: "Midnight Slate",
    background: "#0f172a",
    surface: "#1e293b",
    primary: "#38bdf8",
    secondary: "#818cf8",
    text: "#f8fafc",
  },
  deepEmerald: {
    label: "Deep Emerald",
    background: "#022c22",
    surface: "#064e3b",
    primary: "#34d399",
    secondary: "#a7f3d0",
    text: "#ecfdf5",
  },
  cyberpunkNeon: {
    label: "Cyberpunk Neon",
    background: "#09090b",
    surface: "#18181b",
    primary: "#f43f5e",
    secondary: "#a855f7",
    text: "#fafafa",
  },
};

const routeMeta = {
  "/": { title: "CDR Analyzer" },
  "/analysis": { title: "CDR Analyzer" },
  "/cdr-analyzer": { title: "CDR Analyzer" },
  "/cdr-analysis-software": { title: "CDR Analyzer" },
  "/cdr-analysis-online-free": { title: "CDR Analyzer" },
  "/cdr-call-data-record-pakistan-online-free": { title: "CDR Analyzer" },
  "/cdr-data-online-check": { title: "CDR Analyzer" },
  "/cdr-data-online": { title: "CDR Analyzer" },
  "/call-history-online-free": { title: "CDR Analyzer" },
  "/cdr-analysis": { title: "CDR Analyzer" },
  "/cdr-analysis-tool": { title: "CDR Analyzer" },
  "/blog": { title: "Blog" },
  "/ttd": { title: "Transaction Trail Diagram" },
  "/about-us": { title: "About Us" },
  "/contact-us": { title: "Contact Us" },
  "/privacy-policy": { title: "Privacy Policy" },
  "/cookie-policy": { title: "Cookie Policy" },
  "/terms": { title: "Terms" },
  "/editorial-policy": { title: "Editorial Policy" },
  "/admin": { title: "Admin Panel" },
};

const FEATURE_ACCESS = {
  addVisitLog: ACCESS_PUBLIC,
  cdrAnalyzer: ACCESS_PUBLIC,
  blog: ACCESS_PUBLIC,
  transactionTrail: ACCESS_PUBLIC,
  adminPanel: ACCESS_AUTH,
};

const PROTECTED_ROUTE_PATHS = [
  "admin",
];

const CDR_ANALYZER_ALIAS_PATHS = [
  "cdr-analysis-software",
  "cdr-analysis-online-free",
  "cdr-call-data-record-pakistan-online-free",
  "cdr-data-online-check",
  "cdr-data-online",
  "call-history-online-free",
  "cdr-analysis",
  "cdr-analysis-tool",
];

function PageHeader() {
  const location = useLocation();
  const meta = routeMeta[location.pathname];
  if (!meta) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink
          component={RouterLink}
          to="/"
          underline="hover"
          color="inherit"
        >
          Home
        </MuiLink>
        {location.pathname !== "/" && (
          <Typography color="text.primary">{meta.title}</Typography>
        )}
      </Breadcrumbs>
      <Typography variant="h4" component="h1">
        {meta.title}
      </Typography>
    </Box>
  );
}

function DashboardPage() {
  const [summary, setSummary] = useState({
    activeEnquiries: 0,
    summonsSent: 0,
    summonsSentThisWeek: 0,
    summonsSentLastWeek: 0,
    visitsScheduled: 0,
    visitsScheduledThisWeek: 0,
    enquiriesByYear: [],
    casesByYear: [],
    transferredByYear: [],
    priorityCounts: { High: 0, Medium: 0, Low: 0 },
    antiStateEnquiries: 0,
    antiStateCases: 0,
    personalEnquiriesAssigned: 0,
    personalCasesRegistered: 0,
    challanPending: 0,
  });
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/dashboard/summary`);
        if (!res.ok) throw new Error("Failed to load summary");
        const data = await res.json();
        setSummary({
          activeEnquiries: Number(data.activeEnquiries || 0),
          summonsSent: Number(data.summonsSent || 0),
          summonsSentThisWeek: Number(data.summonsSentThisWeek || 0),
          summonsSentLastWeek: Number(data.summonsSentLastWeek || 0),
          visitsScheduled: Number(data.visitsScheduled || 0),
          visitsScheduledThisWeek: Number(data.visitsScheduledThisWeek || 0),
          enquiriesByYear: Array.isArray(data.enquiriesByYear) ? data.enquiriesByYear : [],
          casesByYear: Array.isArray(data.casesByYear) ? data.casesByYear : [],
          transferredByYear: Array.isArray(data.transferredByYear) ? data.transferredByYear : [],
          priorityCounts: data.priorityCounts || { High: 0, Medium: 0, Low: 0 },
          antiStateEnquiries: Number(data.antiStateEnquiries || 0),
          antiStateCases: Number(data.antiStateCases || 0),
          personalEnquiriesAssigned: Number(data.personalEnquiriesAssigned || 0),
          personalCasesRegistered: Number(data.personalCasesRegistered || 0),
          challanPending: Number(data.challanPending || 0),
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      setNotifLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/notifications`);
        if (!res.ok) throw new Error("Failed to load notifications");
        const data = await res.json();
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      } catch (error) {
        console.error(error);
        setNotifications([]);
      } finally {
        setNotifLoading(false);
      }
    };
    loadNotifications();
  }, []);

  const renderYearChips = (rows) =>
    rows && rows.length ? (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
        {rows.map((item) => (
          <Chip key={`${item.year}`} size="small" label={`${item.year}: ${item.count}`} />
        ))}
      </Stack>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        No data
      </Typography>
    );

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Active Enquiries
              </Typography>
              <Typography variant="h4">
                {loading ? "—" : summary.activeEnquiries}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Enquiries
              </Typography>
              {renderYearChips(summary.enquiriesByYear)}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Active Cases
              </Typography>
              {renderYearChips(summary.casesByYear)}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Transferred Enquiries
              </Typography>
              {renderYearChips(summary.transferredByYear)}
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  label={`High ${summary.priorityCounts?.High ?? 0}`}
                  color="error"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Medium ${summary.priorityCounts?.Medium ?? 0}`}
                  color="warning"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`Low ${summary.priorityCounts?.Low ?? 0}`}
                  color="success"
                  variant="outlined"
                />
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Anti state enquiries: {loading ? "—" : summary.antiStateEnquiries}
                </Typography>
                <Typography variant="body2">
                  Anti state cases charge: {loading ? "—" : summary.antiStateCases}
                </Typography>
                <Typography variant="body2">
                  Personal enquiries assigned: {loading ? "—" : summary.personalEnquiriesAssigned}
                </Typography>
                <Typography variant="body2">
                  Personal cases registered: {loading ? "—" : summary.personalCasesRegistered}
                </Typography>
                <Typography variant="body2">
                  Challan pending: {loading ? "—" : summary.challanPending}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Summons Sent
              </Typography>
              <Typography variant="h4">
                {loading ? "—" : summary.summonsSentLastWeek}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This week: {loading ? "—" : summary.summonsSentThisWeek}
              </Typography>
            </CardContent>
          </Card>
          <Card elevation={1} sx={{ flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">
                Visits Scheduled (This Week)
              </Typography>
              <Typography variant="h4">
                {loading ? "—" : summary.visitsScheduledThisWeek}
              </Typography>
            </CardContent>
          </Card>
        </Stack>
        <Card elevation={1}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Notifications
            </Typography>
            {notifLoading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading notifications…
                </Typography>
              </Stack>
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No notifications.
              </Typography>
            ) : (
              <Stack spacing={2}>
                {notifications.map((item) => (
                  <Card key={item.id} variant="outlined">
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={item.count}
                          color={item.severity || "default"}
                          variant="outlined"
                        />
                        <Typography variant="subtitle1">{item.title}</Typography>
                      </Stack>
                      {item.items?.length ? (
                        <List dense>
                          {item.items.slice(0, 5).map((row, idx) => (
                            <ListItem key={`${item.id}-${idx}`}>
                              <ListItemText
                                primary={`${row.enquirycasenum || row.enquiry_case_num || "Enquiry"} • ${
                                  row.cname || row.person_name || row.accused_name || "Person"
                                }`}
                                secondary={
                                  row.appearance_date ||
                                  row.next_visit_date ||
                                  row.label ||
                                  row.roleofalleged ||
                                  ""
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No recent items.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
}

function TablePage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [priorityData, setPriorityData] = useState({
    High: [],
    Medium: [],
    Low: [],
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const priorities = ["High", "Medium", "Low"];
        const responses = await Promise.all(
          priorities.map((priority) =>
            fetch(
              `${API_BASE}/api/enquiries?priority=${encodeURIComponent(
                priority
              )}&page=1&limit=10`
            )
          )
        );
        const payloads = await Promise.all(
          responses.map((res) => (res.ok ? res.json() : { data: [] }))
        );
        setPriorityData({
          High: payloads[0]?.data || [],
          Medium: payloads[1]?.data || [],
          Low: payloads[2]?.data || [],
        });
      } catch (err) {
        setError("Failed to load priority enquiries.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const renderCard = (title, subtitle, rows, chipColor) => (
    <Card elevation={1} sx={{ flex: 1, minWidth: { xs: "100%", md: 0 } }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1">{title}</Typography>
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Chip size="small" label={`${rows.length}`} color={chipColor} />
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : rows.length ? (
          <List dense sx={{ maxHeight: 360, overflowY: "auto" }}>
            {rows.map((row, idx) => (
              <ListItem key={`${row.enquiryno}-${idx}`} divider>
                <ListItemText
                  primary={row.enquiryno || "—"}
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        {row.nameofcomplainant || row.complainantname || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Status: {row.status || "—"}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No enquiries in this priority.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
          {renderCard(
            "High Priority",
            "Daily update",
            priorityData.High,
            "error"
          )}
          {renderCard(
            "Medium Priority",
            "Weekly update",
            priorityData.Medium,
            "warning"
          )}
          {renderCard(
            "Low Priority",
            "2 Week update",
            priorityData.Low,
            "success"
          )}
        </Stack>
      </Stack>
    </Container>
  );
}

function FormPage({ visitorMode = false }) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { canEdit, canDelete } = useAuth();
  const effectiveCanEdit = visitorMode ? true : canEdit;
  const effectiveCanDelete = visitorMode ? true : canDelete;
  const [visitForm, setVisitForm] = useState({
    enquiryCaseNum: "",
    personType: "Complainant",
    personName: "",
    visitDate: "",
    nextVisitDate: "",
    notes: "",
  });
  const [manualEnquiry, setManualEnquiry] = useState(true);
  const [enquiryOptions, setEnquiryOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [visitLogs, setVisitLogs] = useState([]);
  const [visitPage, setVisitPage] = useState(1);
  const [visitTotalPages, setVisitTotalPages] = useState(0);
  const [visitSearch, setVisitSearch] = useState("");
  const [visitLoading, setVisitLoading] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitExporting, setVisitExporting] = useState(false);
  const [visitUploading, setVisitUploading] = useState(false);
  const [visitUploadProgress, setVisitUploadProgress] = useState(0);
  const visitLimit = 20;

  const getVisitorVisitLogs = () =>
    loadVisitorData(VISITOR_STORAGE_KEYS.visitLogs, []);

  const setVisitorVisitLogs = (rows) => {
    saveVisitorData(VISITOR_STORAGE_KEYS.visitLogs, rows);
  };

  const clearVisitorVisitDraft = () => {
    clearVisitorData(VISITOR_STORAGE_KEYS.visitDraft);
  };

  useEffect(() => {
    if (visitorMode) return;
    const loadEnquiries = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/enquiries/list`);
        if (!res.ok) return;
        const data = await res.json();
        setEnquiryOptions(Array.isArray(data) ? data : []);
      } catch {
        // ignore
      }
    };
    loadEnquiries();
  }, [visitorMode]);

  useEffect(() => {
    if (!visitorMode) return;
    const savedDraft = loadVisitorData(VISITOR_STORAGE_KEYS.visitDraft, null);
    if (savedDraft) {
      setVisitForm((prev) => ({ ...prev, ...savedDraft }));
    }
  }, [visitorMode]);

  useEffect(() => {
    if (!visitorMode) return;
    saveVisitorData(VISITOR_STORAGE_KEYS.visitDraft, visitForm);
  }, [visitorMode, visitForm]);

  const fetchVisitLogs = useCallback(async () => {
    setVisitLoading(true);
    try {
      if (visitorMode) {
        const normalizedSearch = visitSearch.trim().toLowerCase();
        const allRows = getVisitorVisitLogs();
        const filteredRows = normalizedSearch
          ? allRows.filter((row) =>
              [row.enquiry_case_num, row.person_name, row.cnic]
                .map((v) => String(v || "").toLowerCase())
                .some((v) => v.includes(normalizedSearch))
            )
          : allRows;

        const totalEntries = filteredRows.length;
        const totalPages = Math.max(1, Math.ceil(totalEntries / visitLimit));
        const safePage = Math.min(visitPage, totalPages);
        const startIndex = (safePage - 1) * visitLimit;
        const pagedRows = filteredRows.slice(startIndex, startIndex + visitLimit);

        if (safePage !== visitPage) setVisitPage(safePage);
        setVisitLogs(pagedRows);
        setVisitTotalPages(totalPages);
        return;
      }

      const params = new URLSearchParams();
      params.set("page", String(visitPage));
      params.set("limit", String(visitLimit));
      const normalizedSearch = visitSearch.trim();
      if (normalizedSearch) params.set("search", normalizedSearch);
      const res = await fetch(`${API_BASE}/api/visit-logs/list?${params.toString()}`);
      if (!res.ok) return;
      const json = await res.json();
      setVisitLogs(json.data || []);
      setVisitTotalPages(Math.max(1, Math.ceil((json.totalEntries || 0) / visitLimit)));
    } catch {
      // ignore
    } finally {
      setVisitLoading(false);
    }
  }, [visitLimit, visitPage, visitSearch, visitorMode]);

  const normalizeKey = (key) =>
    String(key || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const handleVisitExport = async () => {
    setVisitExporting(true);
    try {
      if (visitorMode) {
        const normalizedSearch = visitSearch.trim().toLowerCase();
        const allRows = getVisitorVisitLogs();
        const filteredRows = normalizedSearch
          ? allRows.filter((row) =>
              [row.enquiry_case_num, row.person_name, row.cnic]
                .map((v) => String(v || "").toLowerCase())
                .some((v) => v.includes(normalizedSearch))
            )
          : allRows;

        const exportRows = filteredRows.map((row, idx) => ({
          "Sr No": idx + 1,
          "Enquiry Case No": row.enquiry_case_num || "",
          "Person Type": row.person_type || "",
          "Person Name": row.person_name || "",
          CNIC: row.cnic || "",
          "Visit Date": row.visit_date || "",
          "Next Visit Date": row.next_visit_date || "",
          Notes: row.notes || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Visit Logs");
        XLSX.writeFile(workbook, "visit_logs_local.xlsx");
        return;
      }

      const limit = 200;
      const baseParams = new URLSearchParams();
      baseParams.set("page", "1");
      baseParams.set("limit", String(limit));
      const normalizedSearch = visitSearch.trim();
      if (normalizedSearch) baseParams.set("search", normalizedSearch);

      const firstRes = await fetch(`${API_BASE}/api/visit-logs/list?${baseParams.toString()}`);
      if (!firstRes.ok) throw new Error("Failed to fetch visit logs");
      const firstJson = await firstRes.json();
      const totalEntries = Number(firstJson.totalEntries || 0);
      const totalPages = Math.max(1, Math.ceil(totalEntries / limit));
      let allRows = firstJson.data || [];

      for (let page = 2; page <= totalPages; page += 1) {
        const pageParams = new URLSearchParams();
        pageParams.set("page", String(page));
        pageParams.set("limit", String(limit));
        if (normalizedSearch) pageParams.set("search", normalizedSearch);
        const res = await fetch(`${API_BASE}/api/visit-logs/list?${pageParams.toString()}`);
        if (!res.ok) throw new Error("Failed to fetch visit logs");
        const json = await res.json();
        allRows = allRows.concat(json.data || []);
      }

      const exportRows = allRows.map((row, idx) => ({
        "Sr No": idx + 1,
        "Enquiry Case No": row.enquiry_case_num || "",
        "Person Type": row.person_type || "",
        "Person Name": row.person_name || "",
        CNIC: row.cnic || "",
        "Visit Date": row.visit_date || "",
        "Next Visit Date": row.next_visit_date || "",
        Notes: row.notes || "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Visit Logs");
      XLSX.writeFile(workbook, "visit_logs.xlsx");
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    } finally {
      setVisitExporting(false);
    }
  };

  const handleVisitImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setVisitUploading(true);
    setVisitUploadProgress(0);
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      const records = rawRows
        .map((row) => {
          const normalized = {};
          Object.entries(row).forEach(([k, v]) => {
            normalized[normalizeKey(k)] = v;
          });
          const get = (...keys) => {
            for (const key of keys) {
              const val = normalized[normalizeKey(key)];
              if (val !== undefined && val !== null && String(val).trim() !== "") return val;
            }
            return "";
          };

          return {
            enquiryCaseNum: get("enquirycase", "enquirycaseno", "enquirycasenum", "enquirycase#"),
            personType: get("persontype", "type"),
            personName: get("personname", "name"),
            visitDate: get("visitdate", "visit"),
            nextVisitDate: get("nextvisitdate", "nextvisit"),
            notes: get("notes", "remark", "remarks"),
          };
        })
        .filter((r) => r.enquiryCaseNum && r.personName && r.visitDate);

      if (!records.length) {
        alert("No valid rows found in the file.");
        return;
      }

      if (visitorMode) {
        const existing = getVisitorVisitLogs();
        const prepared = records.map((record) => ({
          id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          enquiry_case_num: String(record.enquiryCaseNum || "").trim(),
          person_type: String(record.personType || "Complainant").trim() || "Complainant",
          person_name: String(record.personName || "").trim(),
          cnic: "",
          visit_date: String(record.visitDate || "").trim(),
          next_visit_date: String(record.nextVisitDate || "").trim(),
          notes: String(record.notes || "").trim(),
        }));
        setVisitorVisitLogs([...prepared, ...existing]);
        fetchVisitLogs();
        window.dispatchEvent(new CustomEvent("visit-log-created"));
        alert(`Upload complete. Imported ${prepared.length} rows locally.`);
        return;
      }

      const chunkSize = 200;
      for (let i = 0; i < records.length; i += chunkSize) {
        const chunk = records.slice(i, i + chunkSize);
        const res = await fetch(`${API_BASE}/api/visit-logs/bulk-upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: chunk }),
        });
        if (!res.ok) throw new Error("Upload failed");
        const percent = Math.round(((i + chunk.length) / records.length) * 100);
        setVisitUploadProgress(percent);
      }

      alert(`Upload complete. Imported ${records.length} rows.`);
      fetchVisitLogs();
    } catch (error) {
      console.error("Import failed:", error);
      alert("Import failed. Please check the file format.");
    } finally {
      setVisitUploading(false);
      setVisitUploadProgress(0);
      event.target.value = "";
    }
  };

  useEffect(() => {
    fetchVisitLogs();
  }, [fetchVisitLogs]);

  const handleVisitEnquiryChange = async (value, personTypeOverride) => {
    setVisitForm((prev) => ({ ...prev, enquiryCaseNum: value }));
    const personType = personTypeOverride ?? visitForm.personType;
    if (!value || personType !== "Complainant") return;
    if (visitorMode) return;
    try {
      const res = await fetch(`${API_BASE}/api/enquiries/lookup?case=${encodeURIComponent(value)}`);
      if (!res.ok) return;
      const data = await res.json();
      setVisitForm((prev) => ({
        ...prev,
        personName: data.nameofcomplainant || prev.personName,
      }));
    } catch {
      // ignore
    }
  };

  const handleVisitSubmit = async () => {
    if (!effectiveCanEdit) {
      alert("Permission denied");
      return;
    }
    if (!visitForm.enquiryCaseNum || !visitForm.personName || !visitForm.visitDate) {
      alert("Enquiry Case No, Person Name and Visit Date are required.");
      return;
    }
    setSaving(true);
    try {
      if (visitorMode) {
        const nextRow = {
          id: editingVisit?.id || `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          enquiry_case_num: String(visitForm.enquiryCaseNum || "").trim(),
          person_type: String(visitForm.personType || "Complainant").trim() || "Complainant",
          person_name: String(visitForm.personName || "").trim(),
          cnic: editingVisit?.cnic || "",
          visit_date: String(visitForm.visitDate || "").trim(),
          next_visit_date: String(visitForm.nextVisitDate || "").trim(),
          notes: String(visitForm.notes || "").trim(),
        };
        const existing = getVisitorVisitLogs();
        const isEdit = !!editingVisit?.id;
        const updated = isEdit
          ? existing.map((row) => (row.id === editingVisit.id ? { ...row, ...nextRow } : row))
          : [nextRow, ...existing];
        setVisitorVisitLogs(updated);
        clearVisitorVisitDraft();
        window.dispatchEvent(new CustomEvent("visit-log-created"));
        setVisitForm({
          enquiryCaseNum: "",
          personType: "Complainant",
          personName: "",
          visitDate: "",
          nextVisitDate: "",
          notes: "",
        });
        setEditingVisit(null);
        setVisitDialogOpen(false);
        fetchVisitLogs();
        alert(`Visit log ${isEdit ? "updated" : "saved"} locally.`);
        return;
      }

      const isEdit = !!editingVisit?.id;
      const url = isEdit
        ? `${API_BASE}/api/visit-logs/${editingVisit.id}`
        : `${API_BASE}/api/visit-logs`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(visitForm),
      });
      if (!res.ok) {
        alert(`Failed to ${isEdit ? "update" : "save"} visit log.`);
        return;
      }
      window.dispatchEvent(new CustomEvent("visit-log-created"));
      setVisitForm({
        enquiryCaseNum: "",
        personType: "Complainant",
        personName: "",
        visitDate: "",
        nextVisitDate: "",
        notes: "",
      });
      setEditingVisit(null);
      setVisitDialogOpen(false);
      fetchVisitLogs();
      alert(`Visit log ${isEdit ? "updated" : "saved"}.`);
    } finally {
      setSaving(false);
    }
  };

  const openAddVisitDialog = () => {
    if (!effectiveCanEdit) {
      alert("Permission denied");
      return;
    }
    setEditingVisit(null);
    setVisitForm({
      enquiryCaseNum: "",
      personType: "Complainant",
      personName: "",
      visitDate: "",
      nextVisitDate: "",
      notes: "",
    });
    setVisitDialogOpen(true);
  };

  const openEditVisitDialog = (row) => {
    if (!row) return;
    if (!effectiveCanEdit) {
      alert("Permission denied");
      return;
    }
    setEditingVisit(row);
    setVisitForm({
      enquiryCaseNum: row.enquiry_case_num || "",
      personType: row.person_type || "Complainant",
      personName: row.person_name || "",
      visitDate: row.visit_date || "",
      nextVisitDate: row.next_visit_date || "",
      notes: row.notes || "",
    });
    setVisitDialogOpen(true);
  };

  const handleVisitDelete = async (row) => {
    if (!row?.id) return;
    if (!effectiveCanDelete) {
      alert("Permission denied");
      return;
    }
    if (!window.confirm("Delete this visit log?")) return;
    try {
      if (visitorMode) {
        const existing = getVisitorVisitLogs();
        const updated = existing.filter((item) => item.id !== row.id);
        setVisitorVisitLogs(updated);
        fetchVisitLogs();
        window.dispatchEvent(new CustomEvent("visit-log-created"));
        alert("Visit log deleted from local storage.");
        return;
      }

      const res = await fetch(`${API_BASE}/api/visit-logs/${row.id}`, { method: "DELETE" });
      if (!res.ok) {
        alert("Failed to delete visit log.");
        return;
      }
      fetchVisitLogs();
      window.dispatchEvent(new CustomEvent("visit-log-created"));
      alert("Visit log deleted.");
    } catch {
      alert("Failed to delete visit log.");
    }
  };

  return (
    <Container maxWidth="lg">
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
          <Button variant="contained" onClick={openAddVisitDialog} disabled={!effectiveCanEdit}>
            Add Visit Log
          </Button>
          <Button variant="contained" onClick={handleVisitExport} disabled={visitExporting}>
            {visitExporting ? "Exporting..." : visitorMode ? "Export Local Excel" : "Export Excel"}
          </Button>
          <Button variant="contained" color="secondary" component="label" disabled={visitUploading}>
            {visitUploading ? "Uploading..." : visitorMode ? "Import to Local" : "Import Excel"}
            <input type="file" hidden onChange={handleVisitImport} />
          </Button>
          {visitorMode && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                clearVisitorData(VISITOR_STORAGE_KEYS.visitLogs);
                clearVisitorVisitDraft();
                fetchVisitLogs();
              }}
            >
              Clear Local Data
            </Button>
          )}
          <TextField
            label="Search (Name / CNIC / Enquiry No)"
            value={visitSearch}
            onChange={(e) => {
              setVisitSearch(e.target.value);
              setVisitPage(1);
            }}
            sx={{ width: { xs: "100%", md: 180 } }}
          />
        </Stack>

        {visitUploading && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2">Uploading... {visitUploadProgress}%</Typography>
            <LinearProgress variant="determinate" value={visitUploadProgress} />
          </Box>
        )}

        <TableContainer component={Paper} sx={{ width: "100%" }}>
          <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sr No</TableCell>
              <TableCell>Enquiry Case No</TableCell>
              <TableCell>Person Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>CNIC</TableCell>
              <TableCell>Visit Date</TableCell>
              <TableCell>Next Visit Date</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visitLoading ? (
              <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2">Loading...</Typography>
                  </TableCell>
              </TableRow>
            ) : visitLogs.length ? (
              visitLogs.map((row, idx) => (
                  <TableRow
                    key={row.id || `${row.enquiry_case_num}-${idx}`}
                    hover
                    onDoubleClick={() => effectiveCanEdit && openEditVisitDialog(row)}
                    sx={{ cursor: effectiveCanEdit ? "pointer" : "default" }}
                  >
                    <TableCell>{(visitPage - 1) * visitLimit + idx + 1}</TableCell>
                    <TableCell>{row.enquiry_case_num || "—"}</TableCell>
                    <TableCell>{row.person_type || "—"}</TableCell>
                    <TableCell>{row.person_name || "—"}</TableCell>
                    <TableCell>{row.cnic || "—"}</TableCell>
                    <TableCell>{row.visit_date || "—"}</TableCell>
                    <TableCell>{row.next_visit_date || "—"}</TableCell>
                    <TableCell>{row.notes || "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditVisitDialog(row);
                          }}
                          disabled={!effectiveCanEdit}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVisitDelete(row);
                          }}
                          disabled={!effectiveCanDelete}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant="body2">
                      {visitorMode ? "No local visit logs found." : "No visit logs found."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          count={visitTotalPages}
          page={visitPage}
          onChange={(_event, value) => setVisitPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
          sx={{ display: "flex", justifyContent: "center" }}
        />
      </Stack>

      <Dialog open={visitDialogOpen} onClose={() => setVisitDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVisit ? "Edit Visit Log" : "Add Visit Log"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={manualEnquiry}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      if (!checked && enquiryOptions.length === 0) {
                        alert("No enquiry list available. Manual entry will remain enabled.");
                        return;
                      }
                      setManualEnquiry(checked);
                    }}
                  />
                }
                label="Manual entry"
              />
            </Stack>
            {manualEnquiry ? (
              <TextField
                label="Enquiry Case No"
                value={visitForm.enquiryCaseNum}
                onChange={(e) => handleVisitEnquiryChange(e.target.value)}
                fullWidth
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Enquiry Case No</InputLabel>
                <Select
                  label="Enquiry Case No"
                  value={visitForm.enquiryCaseNum}
                  onChange={(e) => handleVisitEnquiryChange(e.target.value)}
                >
                  {enquiryOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth>
              <InputLabel>Person Type</InputLabel>
              <Select
                label="Person Type"
                value={visitForm.personType}
                onChange={(e) => {
                  const value = e.target.value;
                  setVisitForm((prev) => ({ ...prev, personType: value }));
                  if (value === "Complainant" && visitForm.enquiryCaseNum) {
                    handleVisitEnquiryChange(visitForm.enquiryCaseNum, value);
                  }
                }}
              >
                <MenuItem value="Complainant">Complainant</MenuItem>
                <MenuItem value="Alleged">Alleged</MenuItem>
                <MenuItem value="Witness">Witness</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Person Name"
              value={visitForm.personName}
              onChange={(e) => setVisitForm((prev) => ({ ...prev, personName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Visit Date"
              type="date"
              value={visitForm.visitDate}
              onChange={(e) => setVisitForm((prev) => ({ ...prev, visitDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Next Visit Date"
              type="date"
              value={visitForm.nextVisitDate}
              onChange={(e) => setVisitForm((prev) => ({ ...prev, nextVisitDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Notes"
              value={visitForm.notes}
              onChange={(e) => setVisitForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setVisitForm({
                enquiryCaseNum: "",
                personType: "Complainant",
                personName: "",
                visitDate: "",
                nextVisitDate: "",
                notes: "",
              });
              if (visitorMode) clearVisitorVisitDraft();
            }}
          >
            Reset
          </Button>
          <Button onClick={() => setVisitDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVisitSubmit} disabled={saving || !effectiveCanEdit}>
            {saving ? "Saving..." : editingVisit ? "Update Visit Log" : "Save Visit Log"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

function RequireFeature({ access = ACCESS_AUTH, children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }
  if (access === ACCESS_PUBLIC) {
    return children;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
}

function RequireAdmin({ children }) {
  const { role, loading, isAuthenticated } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (role !== "admin") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function LandingPage() {
  return (
    <Container maxWidth="lg">
      <SeoHelmet
        title="CDR Analyzer Pakistan | Free Online CDR Analysis Tool"
        description="Learn how to review authorized Pakistan Call Detail Record files with Cyberpunk, then open the free browser-based analyzer for Excel and CSV data."
        canonical={absoluteUrl("/")}
        ogTitle="CDR Analyzer Pakistan | Cyberpunk"
        ogDescription="A practical guide to authorized CDR analysis, contact patterns, locations, IMEI usage, timelines, and network views."
        ogUrl={absoluteUrl("/")}
        schema={[
          buildWebSiteSchema(),
          buildWebPageSchema({
            name: "CDR Analyzer Pakistan | Free Online CDR Analysis Tool",
            description: "A practical guide to authorized Pakistan CDR analysis and the Cyberpunk browser-based analyzer.",
            path: "/",
          }),
          buildSoftwareApplicationSchema(),
        ]}
      />

      <Stack spacing={3} sx={{ pb: 4 }}>
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h3" component="h1" sx={{ fontSize: { xs: "2rem", sm: "2.8rem" }, mb: 1.5 }}>
            CDR Analyzer for Pakistan
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 850, mb: 2 }}>
            Review authorized Call Detail Record files in your browser and turn complex telecom exports into clear, reproducible investigative summaries.
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 900, mb: 2.5 }}>
            Cyberpunk is a free online CDR analysis workspace for supported Excel and CSV files. It helps users validate operator layouts, normalize phone numbers, compare activity over time, review frequent contacts and locations, inspect IMEI usage, and explore network relationships. The browser processes uploaded files locally for the analysis session; do not upload data you are not authorized to handle.
          </Typography>
          <Button component={RouterLink} to="/analysis" variant="contained" size="large">
            Open CDR Analyzer
          </Button>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h5" component="h2" gutterBottom>What You Can Analyze</Typography>
          <Stack component="ul" spacing={1} sx={{ pl: 3, color: "text.secondary" }}>
            <li>Validate common Jazz, Zong, Ufone, and Telenor CDR layouts, including supported preamble rows.</li>
            <li>Compare incoming and outgoing activity, frequent contacts, short codes, and date or time windows.</li>
            <li>Review LAC, cell ID, site address, latitude, and longitude fields when they are present in the source file.</li>
            <li>Summarize IMEI usage and identify numbers or devices that deserve further lawful review.</li>
            <li>Use the network graph and export tools to document the filtered rows behind an observation.</li>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h5" component="h2" gutterBottom>How to Use the Tool Responsibly</Typography>
          <Typography color="text.secondary" paragraph>
            Begin with a defined purpose, date range, and authorization. Keep the original CDR file unchanged, record the operator and validation results, and treat normalized numbers, frequent contacts, locations, and network connections as analytical leads rather than proof of identity, intent, or wrongdoing.
          </Typography>
          <Typography color="text.secondary" paragraph>
            CDR data can show communication events and approximate network context, but it does not by itself establish who held a device, where a person was continuously located, or what a conversation meant. Corroborate important findings with independent evidence and preserve the source record for review.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Button component={RouterLink} to="/blog" variant="outlined">Read CDR Guides</Button>
            <Button component={RouterLink} to="/about-us" variant="outlined">About the Methodology</Button>
            <Button component={RouterLink} to="/privacy-policy" variant="outlined">Privacy and Data Handling</Button>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Typography variant="h5" component="h2" gutterBottom>CDR Analysis Guides</Typography>
          <Typography color="text.secondary" paragraph>
            The Cyberpunk library explains the decisions behind CDR analysis instead of presenting unexplained scores. Start with the step-by-step workflow, then explore normalization, IMEI, cell-site, reporting, and evidence-handling topics.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button component={RouterLink} to="/blog/how-to-analyze-call-detail-records-step-by-step" variant="outlined">Step-by-Step Workflow</Button>
            <Button component={RouterLink} to="/blog/telecom-investigation-using-excel-cdr-files" variant="outlined">Excel CDR Files</Button>
            <Button component={RouterLink} to="/blog/imei-analysis-from-cdr-investigation-guide" variant="outlined">IMEI Analysis</Button>
            <Button component={RouterLink} to="/blog/evidence-handling-documenting-cdr-findings-court-pakistan" variant="outlined">Evidence Handling</Button>
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}

function MainLayout({ themeKey, setThemeKey }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [tabletOpen, setTabletOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const theme = useTheme();
  const { user, logout, isAuthenticated } = useAuth();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const drawerWidthPx = isMobile
    ? drawerWidth
    : isDesktop
      ? desktopOpen
        ? drawerWidth
        : collapsedDrawerWidth
      : tabletOpen
        ? drawerWidth
        : collapsedDrawerWidth;
  const showDrawerText = isMobile || (isDesktop ? desktopOpen : tabletOpen);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isTablet) {
      setTabletOpen(false);
    }
  }, [isTablet]);

  useEffect(() => {
    if (isDesktop) {
      setDesktopOpen(true);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSearchResults([]);
      setSearchOpen(false);
      return undefined;
    }
    const query = searchText.trim();
    if (query.length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return undefined;
    }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/search?query=${encodeURIComponent(query)}`
        );
        if (!res.ok) throw new Error("Search failed");
        const data = await res.json();
        setSearchResults(Array.isArray(data.results) ? data.results : []);
        setSearchOpen(true);
      } catch (error) {
        console.error(error);
        setSearchResults([]);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [isAuthenticated, searchText]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen((prev) => !prev);
      return;
    }
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
      return;
    }
    setTabletOpen((prev) => !prev);
  };

  const navItems = [
    { to: "/analysis", label: "CDR Analyzer", icon: <AnalyticsIcon />, access: FEATURE_ACCESS.cdrAnalyzer },
    { dividerAfter: true },
    { to: "/ttd", label: "Financial Transaction Trail Diagram", icon: <AccountTreeIcon />, access: FEATURE_ACCESS.transactionTrail },
    { to: "/blog", label: "Blog", icon: <ArticleIcon />, access: FEATURE_ACCESS.blog },
  ];

  if (isAuthenticated && user?.role === "admin") {
    navItems.push({ dividerAfter: true });
    navItems.push({ to: "/admin", label: "Admin Panel", icon: <AdminPanelSettingsIcon />, access: FEATURE_ACCESS.adminPanel });
  }

  const drawerHeader = (
    <Toolbar
      sx={{
        justifyContent: showDrawerText ? "flex-end" : "center",
        px: showDrawerText ? 2 : 1,
      }}
    >
      <IconButton
        onClick={() => {
          if (isMobile) {
            setMobileOpen(false);
            return;
          }
          if (isDesktop) {
            setDesktopOpen((prev) => !prev);
            return;
          }
          setTabletOpen((prev) => !prev);
        }}
        aria-label={isMobile ? "close navigation" : "toggle navigation width"}
      >
        {isMobile
          ? <ChevronLeftIcon />
          : isDesktop
            ? (desktopOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />)
            : (tabletOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />)}
      </IconButton>
    </Toolbar>
  );

  const drawer = (
    <Box>
      {drawerHeader}
      <Divider />
      <List sx={{ pt: 0 }}>
        {navItems.map((item, idx) => {
          if (item.dividerAfter) {
            return (
              <ListItem key={`divider-${idx}`} component="li" disablePadding sx={{ display: "block" }}>
                <Divider sx={{ my: 1 }} />
              </ListItem>
            );
          }
          const isLocked = item.access === ACCESS_AUTH && !isAuthenticated;
          return (
            <Tooltip
              key={item.to}
              title={
                isLocked
                  ? "Login required"
                  : showDrawerText
                    ? ""
                    : item.label
              }
              placement="right"
              disableHoverListener={showDrawerText}
            >
              <ListItem disablePadding>
                <ListItemButton
                  {...(!isLocked
                    ? { component: RouterNavLink, to: item.to, end: item.to === "/" }
                    : {})}
                  disabled={isLocked}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    justifyContent: showDrawerText ? "flex-start" : "center",
                    px: showDrawerText ? 2 : 1.5,
                    "&.active": {
                      backgroundColor: (theme) => theme.palette.action.selected,
                      "& .MuiListItemIcon-root": {
                        color: "primary.main",
                      },
                      "& .MuiListItemText-primary": {
                        color: "text.primary",
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: showDrawerText ? 2 : "auto",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {showDrawerText && <ListItemText primary={item.label} />}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", height: "100vh", width: "100%", overflowX: "hidden" }}>
          <AppBar
            position="fixed"
            color="primary"
            elevation={1}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer + 1,
              width: { sm: `calc(100% - ${drawerWidthPx}px)` },
              ml: { sm: `${drawerWidthPx}px` },
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
                aria-label="toggle navigation"
              >
                <MenuIcon />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                noWrap
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  lineHeight: 1.2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  CDR Analyzer | CDR Analysis | Call Detail Record Analysis
                </Box>
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                  CDR Analyzer
                </Box>
              </Typography>
              {isAuthenticated && (
                <TextField
                  size="small"
                  placeholder="Search enquiries, summons, cases..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{
                    mr: 2,
                    width: { xs: 160, sm: 240, md: 320 },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(255,255,255,0.15)",
                      color: "inherit",
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
                      "&.Mui-focused fieldset": { borderColor: "#fff" },
                    },
                    "& input::placeholder": { color: "rgba(255,255,255,0.7)" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "inherit" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              <FormControl size="small" sx={{ minWidth: 150, mr: 1, display: { xs: "none", sm: "flex" } }}>
                <InputLabel id="theme-select-label" sx={{ color: "inherit" }}>
                  Theme
                </InputLabel>
                <Select
                  labelId="theme-select-label"
                  value={themeKey}
                  label="Theme"
                  onChange={(event) => setThemeKey(event.target.value)}
                  sx={{
                    color: "inherit",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.55)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.9)",
                    },
                    ".MuiSvgIcon-root": { color: "inherit" },
                  }}
                >
                  {Object.entries(APP_THEMES).map(([key, appTheme]) => (
                    <MenuItem key={key} value={key}>
                      {appTheme.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 2 }}>
                {isAuthenticated && user?.username && (
                  <Typography variant="body2" sx={{ color: "inherit" }}>
                    {user.username}
                  </Typography>
                )}
                {isAuthenticated ? (
                  <Button color="inherit" onClick={logout}>
                    Logout
                  </Button>
                ) : (
                  <Button color="inherit" component={RouterLink} to="/login">
                    Login
                  </Button>
                )}
              </Stack>
            </Toolbar>
          </AppBar>

          <Dialog
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Search Results</DialogTitle>
            <DialogContent dividers>
              {searchLoading ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Searching…
                  </Typography>
                </Stack>
              ) : searchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No results for “{searchText.trim()}”.
                </Typography>
              ) : (
                <List dense>
                  {searchResults.map((item, idx) => {
                let to = "/analysis";
                    let primary = "";
                    let secondary = "";
                    if (item.type === "enquiry") {
                      to = "/analysis";
                      primary = item.enquiryno || "Enquiry";
                      secondary = item.nameofcomplainant || item.gistofallegation || "Enquiry";
                    } else if (item.type === "summons") {
                      to = "/analysis";
                      primary = item.enquirycasenum || "Summon";
                      secondary = item.cname || item.cnic || "Summon";
                    } else {
                      to = "/analysis";
                      primary = item.enquiry_case_num || "Case/FIR";
                      secondary = item.accused_name || item.accused_cnic || "Case/FIR";
                    }
                    return (
                      <ListItemButton
                        key={`${item.type}-${idx}`}
                        component={RouterLink}
                        to={to}
                        state={{ search: searchText.trim() }}
                        onClick={() => setSearchOpen(false)}
                      >
                        <ListItemText
                          primary={primary}
                          secondary={`${item.type.toUpperCase()} • ${secondary}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </DialogContent>
          </Dialog>

          <Box
            component="nav"
            sx={{
              width: { sm: drawerWidthPx },
              flexShrink: { sm: 0 },
            }}
            aria-label="main navigation"
          >
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              ModalProps={{ keepMounted: true }}
              sx={{
                display: { xs: "block", sm: "none" },
                "& .MuiDrawer-paper": {
                  width: drawerWidth,
                  boxSizing: "border-box",
                },
              }}
            >
              {drawer}
            </Drawer>
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: "none", sm: "block" },
                "& .MuiDrawer-paper": {
                  width: drawerWidthPx,
                  boxSizing: "border-box",
                  position: "relative",
                  overflowX: "hidden",
                },
              }}
              open
            >
              {drawer}
            </Drawer>
          </Box>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: { xs: "100%", sm: `calc(100% - ${drawerWidthPx}px)` },
              minHeight: "100vh",
              minWidth: 0,
              overflow: "auto",
            }}
          >
            <Toolbar />
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 3 },
                width: "100%",
                "& .MuiContainer-root": { px: 0 },
              }}
            >
              <PageHeader />
              {process.env.NODE_ENV === "development" && (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
                  {/* Layout checks: 320 / 768 / 1024 / 1440 — Current: {viewportWidth}px (
                  {isDesktop ? "desktop" : isTablet ? "tablet" : "mobile"}) */}
                </Typography>
              )}
              <Outlet />
              <Box component="footer" sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                >
                  <Typography variant="caption" color="text.secondary">© {new Date().getFullYear()} Cyberpunk</Typography>
                  <MuiLink component={RouterLink} to="/about-us" underline="hover" variant="caption">About Us</MuiLink>
                  <MuiLink component={RouterLink} to="/contact-us" underline="hover" variant="caption">Contact Us</MuiLink>
                  <MuiLink component={RouterLink} to="/privacy-policy" underline="hover" variant="caption">Privacy Policy</MuiLink>
                  <MuiLink component={RouterLink} to="/cookie-policy" underline="hover" variant="caption">Cookie Policy</MuiLink>
                  <MuiLink component={RouterLink} to="/terms" underline="hover" variant="caption">Terms of Use</MuiLink>
                  <MuiLink component={RouterLink} to="/editorial-policy" underline="hover" variant="caption">Editorial Policy</MuiLink>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
  );
}

function CDRAnalyzerRoute() {
  const seoKeywords = [
    "cdr call data record pakistan online free",
    "cdr data online check",
    "cdr analysis online free",
    "cdr analysis online",
    "cdr data online",
    "cdr analyzer",
    "cdr analysis",
  ];
  return (
    <>
      <SeoHelmet
        title="CDR Analyzer | Online CDR Analysis in Pakistan"
        description="Analyze authorized Pakistan CDR Excel and CSV files online for free. Review calls, contacts, locations, IMEI usage, timelines, and network patterns."
        keywords={seoKeywords}
        canonical={absoluteUrl("/analysis")}
        ogTitle="CDR Analyzer | Online CDR Analysis in Pakistan"
        ogDescription="Check supported Pakistan CDR files online and review contacts, locations, IMEI usage, timelines, and network patterns."
        ogUrl={absoluteUrl("/analysis")}
        schema={[
          buildWebSiteSchema(),
          buildWebPageSchema({
            name: "CDR Analyzer | Online CDR Analysis in Pakistan",
            description: "Analyze authorized Pakistan CDR Excel and CSV files online for free.",
          }),
          buildSoftwareApplicationSchema(),
          buildFaqSchema(CDR_FAQS),
        ]}
      />
      <CDRExcelAnalyzerPage />
      <CdrAeoContent />
    </>
  );
}

function VisitorAwareFormPage() {
  const { isAuthenticated } = useAuth();
  return <FormPage visitorMode={!isAuthenticated} />;
}

function VisitorAwareTransactionTrail() {
  const { isAuthenticated } = useAuth();
  return <TransactionTree visitorMode={!isAuthenticated} />;
}

function FallbackRedirect() {
  const { isAuthenticated } = useAuth();
  return <Navigate to={isAuthenticated ? "/" : "/analysis"} replace />;
}

function App() {
  const [themeKey, setThemeKey] = useState(() => {
    const savedTheme = window.localStorage.getItem("cdr-analyzer-theme");
    return APP_THEMES[savedTheme] ? savedTheme : "cyberpunkNeon";
  });
  const { isAuthenticated, loading } = useAuth();
  const selectedTheme = APP_THEMES[themeKey] || APP_THEMES.midnightSlate;

  useEffect(() => {
    window.localStorage.setItem("cdr-analyzer-theme", themeKey);
  }, [themeKey]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: "dark",
          primary: { main: selectedTheme.primary, contrastText: "#111827" },
          secondary: { main: selectedTheme.secondary, contrastText: "#111827" },
          error: { main: "#d93025" },
          warning: { main: "#f9ab00" },
          info: { main: selectedTheme.primary },
          success: { main: "#188038" },
          background: {
            default: selectedTheme.background,
            paper: selectedTheme.surface,
          },
          text: {
            primary: selectedTheme.text,
            secondary: selectedTheme.text,
          },
        },
        typography: {
          fontFamily: "Roboto, Arial, sans-serif",
        },
        shape: { borderRadius: 8 },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              head: ({ theme }) => ({
                fontWeight: 600,
                backgroundColor: theme.palette.background.paper,
              }),
              root: ({ theme }) => ({
                borderColor: theme.palette.divider,
              }),
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.MuiTableRow-hover:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }),
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
          },
          MuiPaginationItem: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-selected": {
                  backgroundColor: theme.palette.action.selected,
                },
              }),
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: "outlined",
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: ({ theme }) => ({
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
              }),
            },
          },
        },
      }),
    [selectedTheme]
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        {loading ? (
          <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<MainLayout themeKey={themeKey} setThemeKey={setThemeKey} />}>
              <Route index element={<Navigate to="/analysis" replace />} />
              <Route path="form" element={<RequireFeature access={FEATURE_ACCESS.addVisitLog}><VisitorAwareFormPage /></RequireFeature>} />
              <Route path="analysis" element={<RequireFeature access={FEATURE_ACCESS.cdrAnalyzer}><CDRAnalyzerRoute /></RequireFeature>} />
              <Route path="cdr-analyzer" element={<RequireFeature access={FEATURE_ACCESS.cdrAnalyzer}><CDRAnalyzerRoute /></RequireFeature>} />
              {CDR_ANALYZER_ALIAS_PATHS.map((path) => (
                <Route key={path} path={path} element={<Navigate to="/analysis" replace />} />
              ))}
              <Route path="pattern-generator" element={<Navigate to="/analysis" replace />} />
              <Route path="blog" element={<RequireFeature access={FEATURE_ACCESS.blog}><BlogHome /></RequireFeature>} />
              <Route path="blog/category/:category" element={<RequireFeature access={FEATURE_ACCESS.blog}><BlogCategory /></RequireFeature>} />
              <Route path="blog/tag/:tag" element={<RequireFeature access={FEATURE_ACCESS.blog}><BlogTag /></RequireFeature>} />
              <Route path="blog/:slug" element={<RequireFeature access={FEATURE_ACCESS.blog}><BlogPost /></RequireFeature>} />
              <Route path="ttd" element={<RequireFeature access={FEATURE_ACCESS.transactionTrail}><VisitorAwareTransactionTrail /></RequireFeature>} />
              <Route path="about-us" element={<AboutPage />} />
              <Route path="contact-us" element={<ContactPage />} />
              <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="cookie-policy" element={<CookiePolicyPage />} />
              <Route path="terms" element={<TermsPage />} />
              <Route path="editorial-policy" element={<EditorialPolicyPage />} />
              {isAuthenticated ? (
                <>
                  <Route
                    path="admin"
                    element={
                      <RequireAdmin>
                        <AdminPanel />
                      </RequireAdmin>
                    }
                  />
                </>
              ) : (
                PROTECTED_ROUTE_PATHS.map((path) => (
                  <Route key={path} path={path} element={<Navigate to="/login" replace />} />
                ))
              )}
            </Route>
            <Route path="*" element={<FallbackRedirect />} />
          </Routes>
        )}
      </Router>
    </ThemeProvider>
  );
}

export default App;
